import AgentCard from "components/card/AgentCard";
import Search from "./components/Search";
import { useQueryParams } from "hooks/useQueryParams";
import { TiChevronLeftOutline, TiChevronRightOutline } from "react-icons/ti";
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "api/axios";
import { Link, useSearchParams } from "react-router-dom";
import Loader from "components/Loader";
import { Agent } from "types";
import { useTranslation } from "react-i18next";
import PureDropDown from "components/pure-dropdown";
import { AGENT_TYPES_OPTIONS } from "constants/agents";
import { MdUpload } from "react-icons/md";
import ImportAgentModal from "./components/ImportAgentModal";

interface GetAgentsResponse {
  total: number;
  items: Agent[];
}

const AgentsCollection = () => {
  const { t } = useTranslation();
  const { params, updateParams } = useQueryParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsPerPage = 8;
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const permissions = {
    // canCreateAgent: hasPermission(PERMISSIONS.CREATE_AGENT),
    canCreateAgent: true, // Temporarily set to true for testing
  };

  // Get selected agent types from URL params
  const selectedTypes = useMemo(() => {
    const typeParam = searchParams.get("type");
    return typeParam ? typeParam.split(",") : [];
  }, [searchParams]);

  const fetchAgents = useCallback(
    async (signal: AbortSignal) => {
      try {
        // Fetch all agents (or a large number) to allow client-side filtering
        // If API supports query and agent_type filters, we can add them here
        const response = await api.get<GetAgentsResponse>("/agents", {
          params: {
            skip: 0,
            take: 1000, // Fetch a large number to allow client-side filtering
            // TODO: Add query and agent_type params if API supports them
            // ...(params.query && { query: params.query }),
            // ...(selectedTypes.length > 0 && { agent_type: selectedTypes }),
          },
          ...(signal instanceof AbortSignal ? { signal } : {}),
        });
        setAgents(response.data.items);
        setError(null);
      } catch (err) {
        // @ts-expect-error: no prob
        if (err.name === "CanceledError" || signal?.aborted) return;
        setError(t("agents.failedToFetch"));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    fetchAgents(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchAgents]);

  // Prepare filter options from AGENT_TYPES_OPTIONS (actual agent types, not creation categories)
  const typeFilterOptions = useMemo(() => {
    return AGENT_TYPES_OPTIONS.map((option) => ({
      value: option.value,
      label: t(option.labelKey),
    }));
  }, [t]);

  // Handle type filter change
  const handleTypeFilterChange = (value: string | string[]) => {
    const newTypes = Array.isArray(value) ? value : [value];
    const newParams = new URLSearchParams(searchParams);

    if (newTypes.length > 0) {
      newParams.set("type", newTypes.join(","));
    } else {
      newParams.delete("type");
    }

    // Reset to page 1 when filter changes
    newParams.delete("p");
    setSearchParams(newParams, { replace: true });
    updateParams({ page: 1 });
  };

  // Handle clear type filter
  const handleClearTypeFilter = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("type");
    newParams.delete("p");
    setSearchParams(newParams, { replace: true });
    updateParams({ page: 1 });
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(params.query?.trim() || selectedTypes.length > 0);
  }, [params.query, selectedTypes]);

  // Filter agents by name and type
  const filteredAgents = useMemo(() => {
    if (agents.length === 0) return [];

    return agents.filter((agent) => {
      // Filter by name
      const queryLower = params.query?.toLowerCase().trim() || "";
      const matchesName =
        !queryLower || agent.name.toLowerCase().includes(queryLower);

      // Filter by type
      const matchesType =
        selectedTypes.length === 0 ||
        (agent.agent_type && selectedTypes.includes(agent.agent_type));

      return matchesName && matchesType;
    });
  }, [agents, params.query, selectedTypes]);

  // Calculate pagination based on filtered results
  const paginatedAgents = useMemo(() => {
    const startIndex = (params.page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAgents.slice(startIndex, endIndex);
  }, [filteredAgents, params.page, itemsPerPage]);

  const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);

  // Reset to page 1 if current page exceeds total pages after filtering
  useEffect(() => {
    if (totalPages > 0 && params.page > totalPages) {
      updateParams({ page: 1 });
    }
  }, [totalPages, params.page, updateParams]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      updateParams({ page: newPage });
    }
  };

  const handleScheduleStatusChange = useCallback(
    (agentId: string, scheduleStatus?: Agent["schedule_status"]) => {
      setAgents((prevAgents) =>
        prevAgents.map((item) =>
          item.id === agentId
            ? { ...item, schedule_status: scheduleStatus }
            : item
        )
      );
    },
    [setAgents]
  );

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    const abortController = new AbortController();
    fetchAgents(abortController.signal);
  };

  // Handle successful import - refresh agent list
  const handleImportSuccess = useCallback(() => {
    const abortController = new AbortController();
    fetchAgents(abortController.signal);
  }, [fetchAgents]);

  return (
    <div className="relative mt-12 h-[75vh] w-full rounded-[20px] px-6 sm:mt-0">
      <div className="mb-5 w-full">
        <Search
          value={params.query}
          onChange={(value) => updateParams({ query: value, page: 1 })}
          filter={
            <PureDropDown
              options={typeFilterOptions}
              value={selectedTypes}
              onChange={handleTypeFilterChange}
              placeholder={t("agents.filterByType")}
              multiple={true}
              showClearButton={true}
              onClear={handleClearTypeFilter}
            />
          }
          className="items-center"
        >
          {permissions.canCreateAgent && (
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="linear flex h-[45px] shrink-0 items-center justify-center gap-2 rounded-xl bg-green-500 px-4 text-white transition duration-200 hover:bg-green-600 active:bg-green-700"
              title={t("agents.import.button") || "Import Agent"}
            >
              <MdUpload className="text-xl" />
              <span className="flex whitespace-nowrap">
                {t("agents.import.button") || "Import Agent"}
              </span>
            </button>
          )}
        </Search>
      </div>

      {/* Import Modal */}
      <ImportAgentModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportSuccess={handleImportSuccess}
      />

      {loading ? (
        <Loader
          txt={t("agents.loadingAgents")}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      ) : error ? (
        <div className="absolute left-1/2 top-1/2 mt-[38px] flex -translate-x-1/2 -translate-y-1/2  flex-col items-center justify-center gap-4">
          <p className="text-lg font-medium text-red-500">{error}</p>
          <button
            onClick={handleRetry}
            className="rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            {t("common.retry")}
          </button>
        </div>
      ) : filteredAgents.length === 0 ? (
        <div className="absolute left-1/2 top-1/2 mt-[38px] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
          {permissions.canCreateAgent ? (
            <Link
              to="/dashboard/agents/create"
              className="text-lg font-medium text-navy-700 hover:text-navy-900 dark:text-white dark:hover:text-gray-200"
            >
              {hasActiveFilters
                ? t("agents.noAgentsFoundWithFilters")
                : t("agents.noAgentsAvailableClick")}
            </Link>
          ) : (
            <p className="text-lg font-medium text-navy-700 dark:text-white">
              {hasActiveFilters
                ? t("agents.noAgentsFoundWithFilters")
                : t("agents.noAgentsAvailable")}
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4">
          {paginatedAgents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onScheduleStatusChange={handleScheduleStatusChange}
            />
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(params.page - 1)}
            disabled={params.page === 1}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <TiChevronLeftOutline />
          </button>

          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => handlePageChange(index + 1)}
              className={`flex h-10 w-10 items-center justify-center rounded-lg border ${
                params.page === index + 1
                  ? "border-blue-500 bg-blue-500 text-white"
                  : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {index + 1}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(params.page + 1)}
            disabled={params.page === totalPages}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <TiChevronRightOutline />
          </button>
        </div>
      )}
    </div>
  );
};

export default AgentsCollection;
